import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'
import { CommandOptionType } from '~/types/interactions'

export { CommandOptionType, InteractionResponseType } from '~/types/interactions'
export type {
  CommandOptionChoice,
  CommandOption,
  BotCommand,
  RegisterCommandRequest,
  UpdateCommandRequest,
  CommandOptionValue,
  ExecuteCommandRequest,
  RequestAutocompleteRequest,
  InvokeMessageComponentRequest,
  SubmitModalRequest,
  InteractionResult,
  AutocompleteResult,
} from '~/types/interactions'

// Editable draft shape for the command create/edit form (`CommandFormDialog.vue`) — `choices` is
// flattened to a single comma-separated text field for editing, converted to/from
// `CommandOption['choices']` via `parseChoices`/`choicesToText` in `BotDetailPanel.vue`.
export interface OptionFormRow {
  name: string
  description: string
  type: CommandOptionType
  required: boolean
  autocomplete: boolean
  choicesText: string
}

// Rust (`commands/interactions.rs`) turns the server's PermissionDenied "This bot does not respond
// to you" — the bot's reply policy excludes the caller — into this text.
export const BOT_NOT_RESPONDING_MESSAGE = 'Этот бот вам не отвечает'

export function isBotNotRespondingError(error: unknown): boolean {
  return (error as { message?: string } | null)?.message === BOT_NOT_RESPONDING_MESSAGE
}

export function useInteractions() {
  // Keyed by bot id, populated by `getBotCommands` and kept in sync by the CRUD calls below —
  // mirrors the composer's own ad hoc cache (ChatMessageInput.vue) but lives here so the bot
  // management UI and the composer's `/`-autocomplete never see a stale list of each other's edits.
  const commandsByBot = ref<Record<string, BotCommand[]>>({})

  const noopSetError = () => {}

  async function getBotCommands(botId: string): Promise<BotCommand[]> {
    return runAction(noopSetError, { action: 'get_bot_commands', extra: { botId } }, async () => {
      const commands = await invoke<BotCommand[]>('get_bot_commands', { botId })
      commandsByBot.value[botId] = commands
      return commands
    }) as Promise<BotCommand[]>
  }

  // One request for every bot command in the group, disambiguated by BotCommand.bot_id — used by
  // the chat composer's `/`-autocomplete instead of calling getBotCommands once per bot member.
  async function getGroupCommands(groupId: string): Promise<BotCommand[]> {
    return runAction(noopSetError, { action: 'get_group_commands', extra: { groupId } }, async () => {
      const commands = await invoke<BotCommand[]>('get_group_commands', { groupId })
      for (const command of commands) {
        const list = commandsByBot.value[command.bot_id] ?? []
        commandsByBot.value[command.bot_id] = [...list.filter((c) => c.id !== command.id), command]
      }
      return commands
    }) as Promise<BotCommand[]>
  }

  async function registerCommand(request: RegisterCommandRequest): Promise<BotCommand> {
    return runAction(noopSetError, { action: 'register_command', extra: { botId: request.bot_id } }, async () => {
      const command = await invoke<BotCommand>('register_command', { payload: request })
      const list = commandsByBot.value[request.bot_id] ?? []
      commandsByBot.value[request.bot_id] = [...list, command]
      return command
    }) as Promise<BotCommand>
  }

  async function updateCommand(request: UpdateCommandRequest): Promise<BotCommand> {
    return runAction(
      noopSetError,
      { action: 'update_command', extra: { botId: request.bot_id, commandId: request.command_id } },
      async () => {
        const command = await invoke<BotCommand>('update_command', { payload: request })
        const list = commandsByBot.value[request.bot_id] ?? []
        const index = list.findIndex((c) => c.id === command.id)
        if (index !== -1) list[index] = command
        commandsByBot.value[request.bot_id] = [...list]
        return command
      },
    ) as Promise<BotCommand>
  }

  async function deleteCommand(botId: string, commandId: string): Promise<void> {
    await runAction(noopSetError, { action: 'delete_command', extra: { botId, commandId } }, async () => {
      await invoke('delete_command', { botId, commandId })
      commandsByBot.value[botId] = (commandsByBot.value[botId] ?? []).filter((c) => c.id !== commandId)
    })
  }

  async function executeCommand(request: ExecuteCommandRequest): Promise<InteractionResult> {
    return runAction(
      noopSetError,
      { action: 'execute_command', extra: { botId: request.bot_id, commandId: request.command_id } },
      () => invoke<InteractionResult>('execute_command', { payload: request }),
    ) as Promise<InteractionResult>
  }

  async function requestAutocomplete(request: RequestAutocompleteRequest): Promise<AutocompleteResult> {
    return runAction(
      noopSetError,
      { action: 'request_autocomplete', extra: { botId: request.bot_id, commandId: request.command_id } },
      () => invoke<AutocompleteResult>('request_autocomplete', { payload: request }),
    ) as Promise<AutocompleteResult>
  }

  async function invokeMessageComponent(request: InvokeMessageComponentRequest): Promise<InteractionResult> {
    return runAction(
      noopSetError,
      { action: 'invoke_message_component', extra: { botId: request.bot_id, customId: request.custom_id } },
      () => invoke<InteractionResult>('invoke_message_component', { payload: request }),
    ) as Promise<InteractionResult>
  }

  async function submitModal(request: SubmitModalRequest): Promise<InteractionResult> {
    return runAction(
      noopSetError,
      { action: 'submit_modal', extra: { botId: request.bot_id, customId: request.custom_id } },
      () => invoke<InteractionResult>('submit_modal', { payload: request }),
    ) as Promise<InteractionResult>
  }

  return {
    commandsByBot,
    getBotCommands,
    getGroupCommands,
    registerCommand,
    updateCommand,
    deleteCommand,
    executeCommand,
    requestAutocomplete,
    invokeMessageComponent,
    submitModal,
  }
}
